import React from 'react'
import {
	Cell,
	Pie,
	PieChart,
	ResponsiveContainer,
	Sector,
	Tooltip,
} from 'recharts'
import styled from 'styled-components'
import { IProblemStat } from '../../../types/statistics.types'
import useWindowWidth from '../../../utils/use.window.width'

const Wrapper = styled.div`
	width: 100%;
`

const Title = styled.h2`
	font-family: 'e-Ukraine', sans-serif;
	font-weight: 400;
	font-size: 20px;
	line-height: 114%;
	text-align: justify;
	color: #000;
	margin-bottom: 15px;

	@media (max-width: 530px) {
		font-size: 15px;
		margin: 0 0 10px;
	}
`

const DateRange = styled.p`
	font-family: 'e-Ukraine', sans-serif;
	font-weight: 400;
	font-size: 14px;
	line-height: 114%;
	text-align: justify;
	color: #8f8f8f;
	margin: 0 0 20px;

	@media (max-width: 530px) {
		font-size: 12px;
	}
`

const ContentContainer = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
	gap: 20px;
	width: 100%;

	@media (max-width: 768px) {
		flex-direction: column;
		justify-content: center;
	}
`

const ChartWrapper = styled.div`
	width: 50%;
	height: 500px;
	position: relative;

	svg,
	path,
	g {
		outline: none !important;
		focus-visible: none;
	}

	@media (max-width: 768px) {
		width: 100%;
		height: 400px;
	}
	@media (max-width: 425px) {
		width: 100%;
		height: 350px;
	}
`

const TooltipBox = styled.div`
	background: #fff;
	border: 1px solid #d4e8dc;
	border-radius: 6px;
	padding: 8px 12px;
	font-size: 13px;
	color: #1a1a1a;
`

const TooltipTitle = styled.div`
	font-family: 'e-Ukraine-Head', sans-serif;
	font-weight: 400;
	font-size: 14px;
	line-height: 99%;
	color: #555;
	margin-bottom: 5px;
`

const TooltipText = styled.div`
	font-family: 'e-Ukraine-Head', sans-serif;
	font-weight: 600;
	font-size: 12px;
	line-height: 99%;
	color: #555;
`

const CustomTooltip = ({ active, payload }: any) => {
	if (active && payload?.length) {
		return (
			<TooltipBox>
				<TooltipTitle>{payload[0].name}</TooltipTitle>
				<TooltipText>
					{payload[0].value.toLocaleString('uk-UA')} (
					{payload[0].payload.percentage}%)
				</TooltipText>
			</TooltipBox>
		)
	}
	return null
}

export const RenderLegendList = styled.ul`
	list-style: none;
	padding: 0;
	margin: 0;
	width: 45%;
	display: flex;
	flex-direction: column;

	@media (max-width: 768px) {
		width: 100%;
		margin-top: 20px;
	}
`

export const RenderLegendItem = styled.li`
	display: flex;
	align-items: center;
	gap: 15px;
	margin-bottom: 21px;

	@media (max-width: 530px) {
		margin-bottom: 14px;
		gap: 10px;
	}
`

export const RenderLegendBlock = styled.span<{ color: string }>`
	display: inline-block;
	width: 81px;
	height: 16px;
	border-radius: 11px;
	flex-shrink: 0; /* Исправлена опечатка flex-shink */
	background: ${props => props.color};

	@media (max-width: 768px) {
		width: 55px;
		height: 12px;
	}
	@media (max-width: 530px) {
		width: 30px;
		height: 12px;
	}
`

export const RenderLegendText = styled.span`
	font-family: 'e-Ukraine-Head', sans-serif;
	font-weight: 400;
	font-size: 18px;
	line-height: 120%;
	color: #555;

	@media (max-width: 530px) {
		font-size: 12px;
	}
`

interface Props {
	data: IProblemStat[]
	dateRange: string
}

const generateColors = (count: number): string[] => {
	return Array.from({ length: count }, (_, i) => {
		const lightness = 20 + (i / Math.max(count - 1, 1)) * 45
		return `hsl(145, 72%, ${lightness}%)`
	})
}

export const ProblemChartComponent: React.FC<Props> = ({ data, dateRange }) => {
	const windowWidth = useWindowWidth()
	const isMobile = windowWidth <= 530
	const isTablet = windowWidth <= 768
	const totalSum = data.reduce((acc, item) => acc + item.total, 0)

	const chartData = data.map(d => {
		const percent = totalSum > 0 ? Math.round((d.total / totalSum) * 100) : 0
		return {
			name: d.problemName,
			value: d.total,
			percentage: percent,
		}
	})

	const COLORS = generateColors(chartData.length)

	const renderCustomLabel = ({
		cx,
		cy,
		midAngle,
		outerRadius,
		value,
		payload,
	}: any) => {
		const RADIAN = Math.PI / 180

		const radius = outerRadius + (isTablet ? 25 : 28)
		const x = cx + radius * Math.cos(-midAngle * RADIAN)
		const y = cy + radius * Math.sin(-midAngle * RADIAN)

		return (
			<g>
				
				<text
					x={x}
					y={y - 6}
					fill='#555'
					textAnchor='middle'
					dominantBaseline='central'
					fontSize={isTablet ? 12 : 18}
					fontWeight={700}
					fontFamily='e-Ukraine-Head'
				>
					{value.toLocaleString('uk-UA')}
				</text>
				
				<text
					x={x}
					y={y + 8}
					fill='#20c36c'
					textAnchor='middle'
					dominantBaseline='central'
					fontSize={isTablet ? 12 : 16}
					fontWeight={300}
					fontFamily='e-Ukraine-Head'
				>
					{payload.percentage}%
				</text>
			</g>
		)
	}

	const cx = '50%' 
	const cy = '50%'
	const outerRadius = isMobile ? 120 : isTablet ? 150 : 180 
	return (
		<Wrapper>
			<Title>Розподіл звернень по проблемам</Title>
			<DateRange>{dateRange}</DateRange>

			<ContentContainer>
				{/* Левая часть: График */}
				<ChartWrapper>
					<ResponsiveContainer width='100%' height='100%'>
						<PieChart>
							<Pie
								data={chartData}
								cx={cx}
								cy={cy}
								outerRadius={outerRadius}
								dataKey='value'
								labelLine={false}
								label={renderCustomLabel}
								strokeWidth={0}
								style={{ outline: 'none' }} 
								activeShape={(props: any) => {
									
									const {
										cx,
										cy,
										innerRadius,
										outerRadius,
										startAngle,
										endAngle,
										fill,
									} = props
									return (
										<g
											style={{
												filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.2))',
											}}
										>
											<Sector
												cx={cx}
												cy={cy}
												innerRadius={innerRadius}
												outerRadius={outerRadius + 5} 
												startAngle={startAngle}
												endAngle={endAngle}
												fill={fill}
											/>
										</g>
									)
								}}
							>
								{chartData.map((_, index) => (
									<Cell key={index} fill={COLORS[index]} />
								))}
							</Pie>
							<Tooltip content={<CustomTooltip />} />
						</PieChart>
					</ResponsiveContainer>
				</ChartWrapper>

				<RenderLegendList>
					{chartData.map((entry, index) => (
						<RenderLegendItem key={index}>
							<RenderLegendBlock color={COLORS[index]} />
							<RenderLegendText>{entry.name}</RenderLegendText>
						</RenderLegendItem>
					))}
				</RenderLegendList>
			</ContentContainer>
		</Wrapper>
	)
}
